import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Container, Paper, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, FormHelperText,
  Divider, IconButton, Stack, Chip, Stepper, Step, StepLabel,
  FormControlLabel, Checkbox, Alert, useTheme, alpha
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  MenuOpen as MenuOpenIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { sectors, countries } from '../data/marketplaceData';
import Sidebar from '../components/common/Sidebar';
import * as marketplaceApi from '../services/marketplace';
import { useAuth } from '../context/AuthContext';
import { uploadMultipleDocuments } from '../services/upload';

const drawerWidth = 240;

const MainContent = styled(Box)<{ isSidebarOpen: boolean }>(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? drawerWidth : 0,
  width: '100%'
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 3px 10px rgba(0,0,0,0.08)'
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    fontWeight: 500,
    fontSize: '0.9rem'
  },
  '& .MuiStepLabel-label.Mui-active': {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  '& .MuiStepLabel-label.Mui-completed': {
    fontWeight: 600,
  },
}));

const SubmitOpportunityForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    projectName: '',
    sector: '',
    otherSector: '',
    country: '',
    organization: '',
    stage: '',
    summary: '',
    keyActivities: [''],
    productDescription: '',
    advantages: '',
    links: [''],
    documents: [] as File[],
    nextSteps: '',
    termsAgreed: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const stages = [
    "Concept",
    "Feasibility",
    "Ready-to-Build",
    "Operational"
  ];

  const steps = [
    'Project Information',
    'Detailed Description',
    'Supporting Materials',
    'Review & Submit'
  ];


  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleArrayItemChange = (index: number, field: 'keyActivities' | 'links', value: string) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleAddArrayItem = (field: 'keyActivities' | 'links') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveArrayItem = (index: number, field: 'keyActivities' | 'links') => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray.splice(index, 1);
      return { ...prev, [field]: newArray };
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, ...Array.from(event.target.files || [])]
      }));
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData(prev => {
      const newDocuments = [...prev.documents];
      newDocuments.splice(index, 1);
      return { ...prev, documents: newDocuments };
    });
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.projectName.trim()) {
        newErrors.projectName = 'Project name is required';
      }
      if (!formData.sector) {
        newErrors.sector = 'Sector is required';
      }
      if (formData.sector === 'Other' && !formData.otherSector.trim()) {
        newErrors.otherSector = 'Please specify the sector';
      }
      if (!formData.country) {
        newErrors.country = 'Country is required';
      }
      if (!formData.organization.trim()) {
        newErrors.organization = 'Organization name is required';
      }
      if (!formData.stage) {
        newErrors.stage = 'Project stage is required';
      }
    } else if (step === 1) {
      if (!formData.summary.trim()) {
        newErrors.summary = 'Project summary is required';
      } else if (formData.summary.length < 50) {
        newErrors.summary = 'Summary should be at least 50 characters';
      }
      if (formData.keyActivities.some(activity => !activity.trim())) {
        newErrors.keyActivities = 'All key activities must be filled out';
      }
    } else if (step === 2) {
      if (formData.links.some(link => !link.trim())) {
        newErrors.links = 'All links must be filled out';
      }
    } else if (step === 3) {
      if (!formData.termsAgreed) {
        newErrors.termsAgreed = 'You must agree to the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleSubmit = async () => {
    if (validateStep(activeStep)) {
      setIsSubmitting(true);

      try {
        // Upload documents to Cloudinary if any
        let uploadedDocuments: Array<{ url: string; name: string }> = [];
        if (formData.documents.length > 0) {
          try {
            const uploadResult = await uploadMultipleDocuments(formData.documents);
            uploadedDocuments = uploadResult.files.map(file => ({
              url: file.url,
              name: file.originalName
            }));
          } catch (error) {
            console.error('Error uploading documents:', error);
            setIsSubmitting(false);
            setErrors({ submit: 'Failed to upload documents. Please try again.' });
            return;
          }
        }

        // Prepare data for API
        const projectData = {
          projectName: formData.projectName,
          sector: formData.sector === 'Other' ? formData.otherSector : formData.sector,
          country: formData.country,
          owner: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'Unknown',
          organization: formData.organization,
          stage: formData.stage,
          summary: formData.summary,
          keyActivities: formData.keyActivities.filter(a => a.trim()).map(activity => ({ activity })),
          productDescription: formData.productDescription || undefined,
          advantages: formData.advantages || undefined,
          links: formData.links.filter(l => l.trim()).map(url => ({ url, title: url })),
          documents: uploadedDocuments,
          nextSteps: formData.nextSteps || undefined
        };

        await marketplaceApi.createProject(projectData);

        setIsSubmitting(false);
        setIsSuccess(true);

        // Navigate after success
        setTimeout(() => {
          navigate('/marketplace');
        }, 2000);
      } catch (error) {
        console.error('Error submitting project:', error);
        setIsSubmitting(false);
        setErrors({ submit: 'Failed to submit project. Please try again.' });
      }
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Name"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                error={!!errors.projectName}
                helperText={errors.projectName || "Enter a clear, descriptive name for your investment opportunity"}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.sector} required>
                <InputLabel>Sector</InputLabel>
                <Select
                  name="sector"
                  value={formData.sector}
                  onChange={handleSelectChange}
                  label="Sector"
                >
                  {sectors.filter(sector => sector !== "All Sectors").map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      {sector}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                <FormHelperText>{errors.sector || "Select the industry sector of your project"}</FormHelperText>
              </FormControl>
            </Grid>
            {formData.sector === 'Other' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Specify Sector"
                  name="otherSector"
                  value={formData.otherSector}
                  onChange={handleChange}
                  error={!!errors.otherSector}
                  helperText={errors.otherSector}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.country} required>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleSelectChange}
                  label="Country"
                >
                  {countries.filter(country => country !== "All Countries").map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.country || "Select the country where your project is based"}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Organization Name"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                error={!!errors.organization}
                helperText={errors.organization || "Enter your company or organization name"}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.stage} required>
                <InputLabel>Project Stage</InputLabel>
                <Select
                  name="stage"
                  value={formData.stage}
                  onChange={handleSelectChange}
                  label="Project Stage"
                >
                  {stages.map((stage) => (
                    <MenuItem key={stage} value={stage}>
                      {stage}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.stage || "Select the current development stage of your project"}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Summary"
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                error={!!errors.summary}
                helperText={errors.summary || "Provide a concise overview (50-300 characters) of your project, including investment amount sought and key value proposition"}
                required
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Key Activities
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                List the main activities or components of your project
              </Typography>
              {formData.keyActivities.map((activity, index) => (
                <Box key={index} sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    fullWidth
                    label={`Activity ${index + 1}`}
                    value={activity}
                    onChange={(e) => handleArrayItemChange(index, 'keyActivities', e.target.value)}
                    error={!!errors.keyActivities}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    onClick={() => handleRemoveArrayItem(index, 'keyActivities')}
                    disabled={formData.keyActivities.length <= 1}
                    color="error"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddArrayItem('keyActivities')}
                sx={{ mt: 1 }}
              >
                Add Activity
              </Button>
              {errors.keyActivities && (
                <FormHelperText error>{errors.keyActivities}</FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product/Service Description"
                name="productDescription"
                value={formData.productDescription}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Describe your product or service in detail (optional)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Competitive Advantages"
                name="advantages"
                value={formData.advantages}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Describe your unique selling points and advantages (optional)"
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Relevant Links
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add links to your website, pitch deck, or other relevant resources
              </Typography>
              {formData.links.map((link, index) => (
                <Box key={index} sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    fullWidth
                    label={`Link ${index + 1}`}
                    value={link}
                    onChange={(e) => handleArrayItemChange(index, 'links', e.target.value)}
                    error={!!errors.links}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    onClick={() => handleRemoveArrayItem(index, 'links')}
                    disabled={formData.links.length <= 1}
                    color="error"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddArrayItem('links')}
                sx={{ mt: 1 }}
              >
                Add Link
              </Button>
              {errors.links && (
                <FormHelperText error>{errors.links}</FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Supporting Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload documents to support your investment opportunity (business plan, financials, etc.)
              </Typography>
              
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                Upload Files
                <VisuallyHiddenInput type="file" multiple onChange={handleFileChange} />
              </Button>
              
              <Box sx={{ mt: 2 }}>
                {formData.documents.length > 0 ? (
                  <Stack spacing={1}>
                    {formData.documents.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => handleRemoveFile(index)}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No documents uploaded yet
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Next Steps for Interested Investors"
                name="nextSteps"
                value={formData.nextSteps}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Describe what investors should do to proceed with your opportunity (optional)"
              />
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review your information carefully before submitting. Once submitted, our team will review your opportunity within 48 hours.
              </Alert>
              
              <StyledPaper sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Project Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Project Name</Typography>
                    <Typography variant="body1">{formData.projectName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Sector</Typography>
                    <Typography variant="body1">{formData.sector === 'Other' ? formData.otherSector : formData.sector}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                    <Typography variant="body1">{formData.country}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Organization</Typography>
                    <Typography variant="body1">{formData.organization}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Project Stage</Typography>
                    <Typography variant="body1">{formData.stage}</Typography>
                  </Grid>
                </Grid>
              </StyledPaper>
              
              <StyledPaper sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Project Details
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">Summary</Typography>
                <Typography variant="body1" paragraph>{formData.summary}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Key Activities</Typography>
                <ul style={{ marginTop: 4 }}>
                  {formData.keyActivities.map((activity, index) => (
                    activity.trim() && <li key={index}><Typography variant="body1">{activity}</Typography></li>
                  ))}
                </ul>
                
                {formData.productDescription && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Product/Service Description</Typography>
                    <Typography variant="body1" paragraph>{formData.productDescription}</Typography>
                  </>
                )}
                
                {formData.advantages && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">Competitive Advantages</Typography>
                    <Typography variant="body1">{formData.advantages}</Typography>
                  </>
                )}
              </StyledPaper>
              
              <StyledPaper>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Supporting Materials
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Links</Typography>
                <ul style={{ marginTop: 4 }}>
                  {formData.links.map((link, index) => (
                    link.trim() && <li key={index}><Typography variant="body1">{link}</Typography></li>
                  ))}
                </ul>
                
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Documents</Typography>
                {formData.documents.length > 0 ? (
                  <ul style={{ marginTop: 4 }}>
                    {formData.documents.map((file, index) => (
                      <li key={index}><Typography variant="body1">{file.name}</Typography></li>
                    ))}
                  </ul>
                ) : (
                  <Typography variant="body1" color="text.secondary">No documents uploaded</Typography>
                )}
                
                {formData.nextSteps && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Next Steps for Investors</Typography>
                    <Typography variant="body1">{formData.nextSteps}</Typography>
                  </>
                )}
              </StyledPaper>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.termsAgreed}
                    onChange={handleCheckboxChange}
                    name="termsAgreed"
                    color="primary"
                  />
                }
                label="I confirm that all information provided is accurate and I agree to the terms and conditions of the Opportunities Marketplace"
                sx={{ mt: 3 }}
              />
              {errors.termsAgreed && (
                <FormHelperText error>{errors.termsAgreed}</FormHelperText>
              )}
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  if (isSuccess) {
    return (
      <Box sx={{ display: 'flex' }}>
        
        <MainContent isSidebarOpen={false}>
         
          
          <Container maxWidth="md" sx={{ pt: { xs: 8, sm: 12 }, pb: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.9)}, ${alpha(theme.palette.success.light, 0.8)})`,
                textAlign: 'center',
                color: 'white'
              }}
            >
              <CheckIcon sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h4" gutterBottom fontWeight={600}>
                Submission Successful!
              </Typography>
              <Typography variant="body1" paragraph>
                Your investment opportunity has been submitted successfully. Our team will review it within 48 hours and contact you with next steps.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/marketplace')}
                sx={{
                  mt: 2,
                  bgcolor: 'white',
                  color: theme.palette.success.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9)
                  }
                }}
              >
                Return to Marketplace
              </Button>
            </Paper>
          </Container>
        </MainContent>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      
      <MainContent isSidebarOpen={false}>
       
        
        <Container maxWidth="md" sx={{ pt: { xs: 8, sm: 12 }, pb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/marketplace')}
              color="primary"
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="body1">
              Back to Marketplace
            </Typography>
          </Box>
          
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700, mb: 1 }}
          >
            Submit an Investment Opportunity
          </Typography>
          
          <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 4 }}>
            Complete the form below to showcase your project to potential investors. Our team will review your submission within 48 hours.
          </Typography>
          
          <StyledPaper sx={{ mb: 6 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StyledStepLabel>{label}</StyledStepLabel>
                </Step>
              ))}
            </Stepper>
            
            <Divider sx={{ mb: 4 }} />
            
            <form>
              {renderStepContent(activeStep)}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  variant="outlined"
                >
                  Back
                </Button>
                
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Opportunity'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </form>
          </StyledPaper>
        </Container>
      </MainContent>
    </Box>
  );
};

export default SubmitOpportunityForm;